import { hasRequiredRole, normalizeRole } from "@/lib/adminRoles";

export const SETTINGS_ROUTE = "/admin/settings";
export const SETTINGS_PAGE_ROUTE = "/admin/settings/collab-access";

export const NAVIGATION = [
  {
    section: "PILOTAGE",
    basePath: "/admin/pilotage",
    placement: "primary",
    roles: ["ADMIN"],
    items: [
      { label: "Dashboard", path: "/admin/pilotage/dashboard" },
      { label: "Collaborateurs", path: "/admin/pilotage/collaborateurs" },
      { label: "Clients", path: "/admin/pilotage/clients" },
      { label: "Missions", path: "/admin/pilotage/missions" },
      { label: "Timesheets", path: "/admin/pilotage/timesheets" },
      { label: "Frais collaborateurs", path: "/admin/pilotage/frais-collaborateurs" },
      { label: "Objectifs", path: "/admin/pilotage/objectifs" },
      { label: "Charges fixes", path: "/admin/pilotage/charges-fixes" },
      { label: "Rapprochement financier", path: "/admin/pilotage/rapprochement-financier" },
      { label: "Consommation IA", path: "/admin/pilotage/consommation-ia" },
    ],
  },
  {
    section: "RECRUTEMENT",
    basePath: "/admin/recrutement",
    placement: "primary",
    roles: ["ADMIN", "RH"],
    items: [
      { label: "Vue d'ensemble", path: "/admin/recrutement/dashboard" },
      { label: "Besoins clients", path: "/admin/recrutement/besoins" },
      { label: "Clients & Prospects", path: "/admin/recrutement/clients" },
      { label: "Candidats", path: "/admin/recrutement/candidats" },
      { label: "Vivier de talents", path: "/admin/recrutement/vivier" },
      { label: "Suivi des candidatures", path: "/admin/recrutement/candidatures" },
      { label: "Entretiens", path: "/admin/recrutement/entretiens" },
      { label: "Communications", path: "/admin/recrutement/communications" },
      { label: "CVs", path: "/admin/recrutement/cv-library" },
      { label: "Simulation de recrutement", path: "/admin/recrutement/simulation" },
    ],
  },
  {
    section: "CARRIÈRES",
    basePath: "/admin/recrutement/offres",
    placement: "primary",
    roles: ["ADMIN", "RH"],
    items: [
      { label: "Offres publiées", path: "/admin/recrutement/offres" },
      { label: "Candidatures reçues via le site", path: "/admin/recrutement/candidatures?tab=public" },
      { label: "Statistiques des offres et candidatures", path: "/admin/recrutement/statistiques" },
    ],
  },
  {
    section: "Mon espace",
    basePath: "/collab",
    placement: "primary",
    roles: ["COLLAB"],
    items: [
      { label: "Ma mission", path: "/collab" },
    ],
  },
  {
    section: "ADMINISTRATION",
    basePath: SETTINGS_ROUTE,
    placement: "primary",
    roles: ["ADMIN"],
    items: [
      { label: "Paramètres", path: SETTINGS_PAGE_ROUTE },
    ],
  },
];

const LEGACY_EXACT_ROUTES = {
  "/admin/dashboard": "/admin/pilotage/dashboard",
  "/admin/collaborateurs": "/admin/pilotage/collaborateurs",
  "/admin/clients": "/admin/pilotage/clients",
  "/admin/missions": "/admin/pilotage/missions",
  "/admin/charges-fixes": "/admin/pilotage/charges-fixes",
  "/admin/objectifs": "/admin/pilotage/objectifs",
  "/admin/simulation": "/admin/recrutement/simulation",
  "/admin/pilotage/simulation": "/admin/recrutement/simulation",
  "/admin/offres": "/admin/recrutement/offres",
  "/admin/candidatures": "/admin/recrutement/candidatures",
  "/admin/stats": "/admin/recrutement/statistiques",
  "/admin/recrutement": "/admin/recrutement/dashboard",
  "/recrutement": "/admin/recrutement/dashboard",
  "/recrutement/dashboard": "/admin/recrutement/dashboard",
  "/recrutement/statistiques": "/admin/recrutement/statistiques",
  "/recrutement/offres": "/admin/recrutement/offres",
  "/recrutement/candidatures": "/admin/recrutement/candidatures",
  "/admin/recrutement/cv": "/admin/recrutement/cv-library",
  "/admin/recrutement/cvs": "/admin/recrutement/cv-library",
  "/admin/recrutement/postulations": "/admin/recrutement/candidatures?tab=public",
  "/admin/recrutement/candidatures-site": "/admin/recrutement/candidatures?tab=public",
};

const LEGACY_PREFIX_ROUTES = [
  ["/admin/missions/", "/admin/pilotage/missions/"],
  ["/recrutement/", "/admin/recrutement/"],
];

const isExactPath = (pathname, expectedPath) => {
  const cleanExpected = expectedPath.split(/[?#]/)[0];
  return pathname === cleanExpected || pathname.startsWith(`${cleanExpected}/`);
};

export const resolveNavigationPath = (path = "") => {
  const pathname = String(path || "").split(/[?#]/)[0] || "/";
  const exactPath = LEGACY_EXACT_ROUTES[pathname];

  if (exactPath) {
    return exactPath;
  }

  const prefixedRoute = LEGACY_PREFIX_ROUTES.find(([legacyPath]) =>
    pathname.startsWith(legacyPath),
  );

  if (prefixedRoute) {
    const [legacyPath, nextBasePath] = prefixedRoute;
    return pathname.replace(legacyPath, nextBasePath);
  }

  return pathname;
};

export const getNavigationSectionsForRole = (role, placement = null) =>
  NAVIGATION.filter((section) => {
    if (!hasRequiredRole(role, section.roles)) {
      return false;
    }

    return placement ? section.placement === placement : true;
  });

export const resolveNavigationContext = (path = "") => {
  const pathname = resolveNavigationPath(path);
  const fullPath = String(path || "");

  if (pathname.startsWith("/admin/pilotage")) {
    return "pilotage";
  }

  if (
    pathname.startsWith("/admin/recrutement/offres") ||
    pathname.startsWith("/admin/recrutement/statistiques") ||
    (pathname.startsWith("/admin/recrutement/candidatures") && fullPath.includes("tab=public"))
  ) {
    return "carrieres";
  }

  if (pathname.startsWith("/admin/recrutement")) {
    return "recrutement";
  }

  if (pathname.startsWith("/collab")) {
    return "collab";
  }

  if (pathname.startsWith(SETTINGS_ROUTE)) {
    return "administration";
  }

  return null;
};

export const getRoleHomePath = (role) => {
  const normalizedRole = normalizeRole(role);

  if (normalizedRole === "SUPER_ADMIN" || normalizedRole === "ADMIN") {
    return "/admin/pilotage/dashboard";
  }

  if (normalizedRole === "RH") {
    return "/admin/recrutement/dashboard";
  }

  return "/collab";
};

const ADDITIONAL_ROUTE_RULES = [
  {
    matcher: (pathname) =>
      pathname === "/admin" || pathname === "/admin/" || pathname.startsWith("/admin/settings"),
    roles: ["ADMIN"],
  },
  {
    matcher: (pathname) => pathname.startsWith("/admin/pilotage"),
    roles: ["ADMIN"],
  },
  {
    matcher: (pathname) => pathname.startsWith("/admin/recrutement"),
    roles: ["ADMIN", "RH"],
  },
  {
    matcher: (pathname) => pathname.startsWith("/admin/outils"),
    roles: ["ADMIN", "RH"],
  },
  {
    matcher: (pathname) => pathname.startsWith("/collab"),
    roles: ["COLLAB"],
  },
];

export const canAccessRoute = (role, path = "") => {
  const pathname = resolveNavigationPath(path);
  const matchingSection = NAVIGATION.find((section) => isExactPath(pathname, section.basePath));

  if (matchingSection) {
    return hasRequiredRole(role, matchingSection.roles);
  }

  const matchingItem = NAVIGATION.flatMap((section) =>
    section.items.map((item) => ({ ...item, roles: section.roles })),
  ).find((item) => isExactPath(pathname, item.path));

  if (matchingItem) {
    return hasRequiredRole(role, matchingItem.roles);
  }

  const matchingRule = ADDITIONAL_ROUTE_RULES.find((rule) => rule.matcher(pathname));

  if (matchingRule) {
    return hasRequiredRole(role, matchingRule.roles);
  }

  return false;
};

export const getNavigationMatch = (path = "") => {
  const pathname = resolveNavigationPath(path);

  for (const section of NAVIGATION) {
    const matchedItem = section.items.find((item) => isExactPath(pathname, item.path));

    if (matchedItem) {
      return {
        section,
        item: matchedItem,
        pathname,
      };
    }

    if (isExactPath(pathname, section.basePath)) {
      return {
        section,
        item: null,
        pathname,
      };
    }
  }

  return null;
};

const isTechnicalIdentifier = (segment) => {
  if (!segment) return false;
  // MongoDB ObjectID (24 hex characters)
  if (/^[0-9a-fA-F]{24}$/.test(segment)) return true;
  // Standard UUID format
  if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/i.test(segment)) return true;
  // Generic hex or numeric hash string longer than 12 chars
  if (/^[0-9a-fA-F]{16,}$/.test(segment)) return true;
  return false;
};

const humanizeSegment = (segment) => {
  if (!segment) {
    return "";
  }

  if (isTechnicalIdentifier(segment)) {
    return "Fiche";
  }

  return segment
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getBreadcrumbItems = (path = "") => {
  const match = getNavigationMatch(path);

  if (match?.item) {
    const items = [
      { label: match.section.section, path: match.section.basePath },
      { label: match.item.label, path: match.item.path },
    ];

    if (match.pathname !== match.item.path) {
      const remainingPath = match.pathname.slice(match.item.path.length).replace(/^\/+/, "");
      const segments = remainingPath.split("/").filter(Boolean);

      segments.forEach((segment, index) => {
        items.push({
          label: index === segments.length - 1 ? humanizeSegment(segment) : "Détail",
          path: null,
        });
      });
    }

    return items;
  }

  if (match?.section) {
    return [
      { label: match.section.section, path: match.section.basePath },
    ];
  }

  if (resolveNavigationPath(path).startsWith("/admin/settings")) {
    return [
      { label: "Administration", path: "/admin" },
      { label: "Paramètres", path: "/admin/settings/collab-access" },
    ];
  }

  return [];
};
